import { formatDateString } from "@cp/toolkit";
import { File_Key_Document_Role } from "@qw/graphql";
import { groupBy, partition, uniqBy } from "lodash";
import { FLAGGED_FILE_LABEL } from "../components/files/flagged-file-label";
import { allAncestorLabels, type TagWithParents } from "./build-tag-tree";
import { type DisplayTypeFile, fileDisplayType } from "./file-display-type";

export const SMALL_IMAGE_MAX_BYTES = 50_000;

// Outlook names images embedded in an email body `image001.png`, `image002.jpg`, …; they're signatures and logos.
export const INLINE_IMAGE_NAME = /^image\d+\.(png|jpe?g|gif|bmp)$/i;

// Mail clients name attachments they couldn't name: forwarded messages, header dumps, Gmail reactions, inline images.
export const UNNAMED_ATTACHMENT_NAMES: ReadonlySet<string> = new Set(["unknown", "noname"]);

export const PINNED_ROLES_INTERNAL: readonly File_Key_Document_Role[] = [
  File_Key_Document_Role.ProcessedQuote,
  File_Key_Document_Role.ProcessedBinder,
  File_Key_Document_Role.Policy,
  File_Key_Document_Role.StampedPolicy,
  File_Key_Document_Role.Acord_125,
];

// Processed quotes and binders are internal-only, so agents get the redacted versions pinned as well.
export const PINNED_ROLES_EXTERNAL: readonly File_Key_Document_Role[] = [
  ...PINNED_ROLES_INTERNAL,
  File_Key_Document_Role.RedactedQuote,
  File_Key_Document_Role.RedactedBinder,
];

export interface GroupableFile extends DisplayTypeFile {
  id: string;
  createdAt: string;
  deletedAt?: string | null;
  filename: string;
  labels: TagWithParents[];
  mimeType: string;
  size: number;
}

export interface GroupOptions {
  internal: boolean;
  query: string;
  tag: string;
}

export interface InsuredFileYear<T> {
  year: string;
  /** Pinned key documents for the year, shown before its other files. */
  keyDocuments: T[];
  files: T[];
  /** Email-signature and inline images for the year, behind a "More files" toggle. */
  moreFiles: T[];
}

export interface InsuredFileGroups<T> {
  flagged: T[];
  years: InsuredFileYear<T>[];
}

export const displayedDate = (file: Pick<GroupableFile, "createdAt" | "deletedAt">) =>
  new Date(file.deletedAt ?? file.createdAt);

const matchesTag = (file: GroupableFile, tag: string) =>
  file.labels.some((label) => label.label === tag || allAncestorLabels(label.parentTags).includes(tag));

const matchesQuery = (file: GroupableFile, query: string) =>
  [file.filename, fileDisplayType(file), ...file.labels.map(({ label }) => label)].some((text) =>
    text?.toLowerCase().includes(query)
  );

const isMoreFile = (file: GroupableFile) =>
  (file.mimeType.startsWith("image/") && file.size < SMALL_IMAGE_MAX_BYTES) ||
  INLINE_IMAGE_NAME.test(file.filename) ||
  UNNAMED_ATTACHMENT_NAMES.has(file.filename.trim().toLowerCase());

/**
 * Filters by folder tag and search query, then splits into display sections, newest first.
 * Each year leads with its key documents and tucks inline images, small images and unnamed attachments into its more files.
 * Flagged files also appear in their year; every other file appears once.
 */
export const groupInsuredFiles = <T extends GroupableFile>(
  files: readonly T[],
  { internal, query, tag }: GroupOptions
): InsuredFileGroups<T> => {
  const normalizedQuery = query.trim().toLowerCase();
  const pinnedRoles = internal ? PINNED_ROLES_INTERNAL : PINNED_ROLES_EXTERNAL;
  const isPinned = (file: T) => !!file.keyDocumentRole && pinnedRoles.includes(file.keyDocumentRole);

  const visible = files
    .filter((file) => !tag || matchesTag(file, tag))
    .filter((file) => !normalizedQuery || matchesQuery(file, normalizedQuery))
    .sort((a, b) => displayedDate(b).getTime() - displayedDate(a).getTime());

  // Read the year off the ISO date, as before, so a just-after-midnight UTC timestamp keeps its year in US timezones.
  const filesByYear = groupBy(visible, (file) => formatDateString(file.deletedAt ?? file.createdAt, "yyyy"));

  return {
    flagged: visible.filter((file) => file.labels.some(({ label }) => label === FLAGGED_FILE_LABEL)),
    years: Object.keys(filesByYear)
      .sort()
      .reverse()
      .map((year) => {
        const [keyDocuments, rest] = partition(filesByYear[year], isPinned);
        const [moreFiles, files] = partition(rest, isMoreFile);
        return { year, keyDocuments, files, moreFiles };
      }),
  };
};

export const isEmptyGroups = <T>({ flagged, years }: InsuredFileGroups<T>) => flagged.length + years.length === 0;

/** Preview navigation follows display order; a flagged file is visited once, and a year's more files only when it is expanded. */
export const previewOrder = <T extends { id: string }>(
  groups: InsuredFileGroups<T>,
  isExpanded: (year: string) => boolean
) =>
  uniqBy(
    [
      ...groups.flagged,
      ...groups.years.flatMap(({ year, keyDocuments, files, moreFiles }) => [
        ...keyDocuments,
        ...files,
        ...(isExpanded(year) ? moreFiles : []),
      ]),
    ],
    (file) => file.id
  );

/** The section each previewed file sits under on the Files page: a flagged file under its flag, every other under its year. */
export const previewGroupHeadings = <T extends { id: string }>(groups: InsuredFileGroups<T>) => {
  const headings = new Map<string, string>();

  for (const file of groups.flagged) {
    headings.set(file.id, FLAGGED_FILE_LABEL);
  }
  for (const { year, keyDocuments, files, moreFiles } of groups.years) {
    for (const file of [...keyDocuments, ...files, ...moreFiles]) {
      if (!headings.has(file.id)) {
        headings.set(file.id, year);
      }
    }
  }

  return headings;
};
